import { Form } from "react-bootstrap";
import { SubTask, SubTaskGroup, SubTaskPrompt, SubTaskRole, TaskSession } from "../../interfaces/Task";
import SubTaskPromptPreview from "./SubTaskPromptPreview";
import Moment from 'moment';
import { useNavigate } from "react-router-dom";
import SiteSubtaskGroup from "./SiteSubtaskGroup";
import SiteSubTaskRole from "./SiteSubTaskRole";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";

export interface PreviewSubTaskProps {
    task_id: string;
    subtask: SubTask;
    sessions: TaskSession[]
}

export default function PreviewSubTask(props: PreviewSubTaskProps) {
    const { t } = useTranslation();

    const contentRef = useRef<HTMLDivElement>(null);
    const reactToPrintFn = useReactToPrint({ contentRef });
    
    const navigate = useNavigate();
    const handleCalendarChange = (event: any) => {
        const body = { session: event.target.value };
        const requestOptionsPost = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(body)
        };
        const reqUrl = import.meta.env.VITE_API_URL + '/v1' +
            '/task/' + props.task_id +
            '/subtask/' + props.subtask._id +
            '/session';
        fetch(reqUrl, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {});
    }

    const handleStudentsChange = (event: any) => {
        const body = { students_session: event.target.checked };
        const requestOptionsPost = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(body)
        };
        const reqUrl = import.meta.env.VITE_API_URL + '/v1' +
            '/task/' + props.task_id +
            '/subtask/' + props.subtask._id +
            '/students';
        fetch(reqUrl, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {});
    }

    const handleTeachersChange = (event: any) => {
        const body = { teachers_session: event.target.checked };
        const requestOptionsPost = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('token')
            },
            body: JSON.stringify(body)
        };
        const reqUrl = import.meta.env.VITE_API_URL + '/v1' +
            '/task/' + props.task_id +
            '/subtask/' + props.subtask._id +
            '/teachers';
        fetch(reqUrl, requestOptionsPost)
            .then(response => {
                if (response.status !== 200) {
                    navigate('/');
                }
                return response.json();
            })
            .then(data => {});
    }

    return(
        <div className="preview-subtask-item">
            <div className="inner">
                <div className="subtask-name">
                    <h4>{ props.subtask.name }</h4>
                    <button className="print-button" onClick={reactToPrintFn}>{ t("Print") }</button>
                </div>
                { props.subtask.goal &&
                    <div className="goal">
                        { props.subtask.goal }
                    </div>
                }
                <div className="subtask-options">
                    <Form.Group controlId="calendar" className="calendar">
                        <Form.Select name="session" onChange={ handleCalendarChange }>
                            <option value="">{ t("- Select a session -") }</option>
                            { props.sessions.map((s: TaskSession) => {
                                return <option selected={ props.subtask.session == s._id } value={ s._id }>{ s.name + ' (' + Moment(s.datetime).format('DD/MM/YYYY') + ')' }</option>
                            })}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="field-student-share">
                        <Form.Check type="checkbox" id={ 'share-with-students-' + props.subtask._id } 
                            key={ 'share-with-students-' + props.subtask._id } label={ t("Students") }
                            onChange={ handleStudentsChange } defaultChecked={ props.subtask.students_session }
                        />
                    </Form.Group>
                    <Form.Group className="field-teachers-share">
                        <Form.Check type="checkbox" id={ 'share-with-teachers-' + props.subtask._id } 
                            key={ 'share-with-teachers-' + props.subtask._id } label={ t("Teachers") }
                            onChange={ handleTeachersChange } defaultChecked={ props.subtask.teachers_session }
                        />
                    </Form.Group>
                </div>
                <div className="subtask-content" ref={ contentRef }>
                    <h4 className="subtask-title">{ props.subtask.name }</h4>
                    <div className="groups">
                        <h5>{ t("Groups") }</h5>
                        { props.subtask.groups.length == 0 &&
                            <div className="message error">
                                { t("There are no groups") }
                            </div>
                        }
                        { props.subtask.groups.length > 0 &&
                            <div className="groups-list">
                                <div className="inner">
                                    { props.subtask.groups.map((g: SubTaskGroup) => {
                                        return <SiteSubtaskGroup group={ g }  prompts={ props.subtask.prompts } rols={ props.subtask.roles }/>
                                    }) }
                                </div>
                            </div>
                        }
                    </div>
                    <div className="roles">
                        <h5>{ t("Roles") }</h5>
                        { props.subtask.roles.length == 0 &&
                            <div className="message error">
                                { t("There are no roles") }
                            </div>
                        }
                        { props.subtask.roles.length > 0 &&
                            <div className="roles-list">
                                <div className="inner">
                                    { props.subtask.roles.map((r: SubTaskRole) => {
                                        return <SiteSubTaskRole role={ r } subTask={ props.subtask } />
                                    }) }
                                </div>
                            </div>
                        }
                    </div>
                    <div className="prompts">
                        <h5>{ t("Prompts") }</h5>
                        { props.subtask.prompts.length == 0 &&
                            <div className="message error">
                                { t("There are no prompts defined") }
                            </div>
                        }
                        { props.subtask.prompts.length > 0 &&
                            props.subtask.prompts.map((p: SubTaskPrompt) => {
                                return <SubTaskPromptPreview task_id={ props.task_id }
                                    subtask_id={ props.subtask._id }
                                    prompt={ p } />
                            })
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}